import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StateShape as ReduxStateShape } from '../../reducers';
import { actionCreators as settingsActionCreators } from '../../reducers/settings';
import { TabData, getTabsConfiguration } from './tabsConfig';
import TabRenderer from './TabRenderer';
import {
    processSettingsBeforeDisplay,
    processSettingsBeforeSave,
    getEnvironmentSpecificData,
    getChangedProperties,
} from './dataProcessing';

const TabbedSettingsForm = () => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('basic');
    const [activeSubTab, setActiveSubTab] = useState<{ [key: string]: string }>({});
    const [settingsSaved, setSettingsSaved] = useState(false);
    const [formData, setFormData] = useState<any>({});
    const [originalSettings, setOriginalSettings] = useState<any>({});
    const [isInitialized, setIsInitialized] = useState(false);

    const { settings } = useSelector((state: ReduxStateShape) => ({
        settings: state.settings.settings,
    }));

    const processedSettings = processSettingsBeforeDisplay(settings);

    const tabs = getTabsConfiguration(processedSettings?.environments || []);

    // Initialize form data when settings are loaded (only once)
    useEffect(() => {
        if (processedSettings && !isInitialized) {
            const initialFormData: any = {};
            const initialSubTabs: { [key: string]: string } = {};

            const initializeTabData = (tabs: TabData[], parentKey?: string) => {
                tabs.forEach((tab) => {
                    const tabData = getTabData(tab.key);
                    initialFormData[tab.key] = tabData;

                    if (tab.subTabs && tab.subTabs.length > 0) {
                        const subTabKey = parentKey ? `${parentKey}.${tab.key}` : tab.key;
                        initialSubTabs[subTabKey] = tab.subTabs[0].key;
                        initializeTabData(tab.subTabs, subTabKey);
                    }
                });
            };

            initializeTabData(tabs);

            setFormData(initialFormData);
            setActiveSubTab(initialSubTabs);
            setOriginalSettings(JSON.parse(JSON.stringify(processedSettings)));
            setIsInitialized(true);
        }
    }, [settings, isInitialized]);

    const getTabData = (tabKey: string) => {
        if (!processedSettings) {
            return {};
        }

        const tab = findTabByKey(tabs, tabKey);
        if (!tab) {
            return {};
        }

        const tabData: any = {};
        const schemaProperties = tab.schema.properties || {};

        Object.keys(schemaProperties).forEach((key) => {
            if (processedSettings[key] !== undefined) {
                tabData[key] = processedSettings[key];
            }
        });

        return tabData;
    };

    const findTabByKey = (tabs: TabData[], key: string): TabData | null => {
        for (const tab of tabs) {
            if (tab.key === key) return tab;
            if (tab.subTabs) {
                const found = findTabByKey(tab.subTabs, key);
                if (found) return found;
            }
        }
        return null;
    };

    const handleFormChange = (tabKey: string, data: any) => {
        setFormData((prev: any) => {
            const newFormData = {
                ...prev,
                [tabKey]: data.formData,
            };

            const parentTabKey = tabKey.includes('-') ? tabKey.split('-')[0] : tabKey;
            if (parentTabKey !== tabKey) {
                newFormData[parentTabKey] = data.formData;
            }

            return newFormData;
        });
    };

    const handleTabSave = (tabKey: string, formSubmitData: any) => {
        const currentTabData = formSubmitData.formData;

        const cleanedData: any = {};
        Object.keys(currentTabData).forEach((key) => {
            const value = currentTabData[key];

            if (Array.isArray(value)) {
                if (value.length > 0) {
                    cleanedData[key] = value;
                }
            } else if (value !== null && value !== undefined && value !== '') {
                cleanedData[key] = value;
            }
        });

        const processedTabData = processSettingsBeforeSave(cleanedData);

        dispatch(settingsActionCreators.saveSettings(processedTabData));

        // Update form data with the saved data to reflect changes immediately
        setFormData((prev: any) => ({
            ...prev,
            [tabKey]: currentTabData,
        }));

        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
    };

    const handleEnvironmentSpecificSave = (envId: string, formSubmitData: any) => {
        const currentTabData = formSubmitData.formData;
        const tabKey = `environments-security-csp-${envId}`;

        if (!currentTabData.environments || currentTabData.environments.length === 0) {
            return;
        }

        const updatedEnvData = currentTabData.environments[0];
        const updatedSettings = { ...processedSettings };
        const envIndex = updatedSettings.environments.findIndex((env: any) => env.id === envId);

        if (envIndex !== -1) {
            updatedSettings.environments[envIndex] = {
                ...updatedSettings.environments[envIndex],
                ...updatedEnvData,
            };
        }

        const fullSettingsToSave = {
            ...processedSettings,
            environments: updatedSettings.environments,
        };

        const processedData = processSettingsBeforeSave(fullSettingsToSave);

        dispatch(settingsActionCreators.saveSettings(processedData));

        // Update form data with the saved data to reflect changes immediately
        setFormData((prev: any) => ({
            ...prev,
            [tabKey]: currentTabData,
        }));

        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
    };

    const handleSave = () => {
        const mergedData = { ...processedSettings };

        Object.keys(formData).forEach((tabKey) => {
            if (formData[tabKey]) {
                Object.assign(mergedData, formData[tabKey]);
            }
        });

        const processedData = processSettingsBeforeSave(mergedData);
        const changedData = getChangedProperties(originalSettings, processedData);

        if (Object.keys(changedData).length > 0) {
            dispatch(settingsActionCreators.saveSettings(changedData));
            setSettingsSaved(true);
            setTimeout(() => setSettingsSaved(false), 3000);
            setOriginalSettings(JSON.parse(JSON.stringify(processedData)));

            // Update form data to reflect the saved changes
            const updatedFormData: any = {};
            Object.keys(formData).forEach((tabKey) => {
                updatedFormData[tabKey] = formData[tabKey];
            });
            setFormData(updatedFormData);
        }
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleSubTabChange = (parentKey: string, subKey: string) => {
        setActiveSubTab((prev) => ({ ...prev, [parentKey]: subKey }));
    };

    const handleFormSubmit = (tabKey: string, data: any) => {
        if (tabKey.includes('environments-security-csp-')) {
            const envId = tabKey.replace('environments-security-csp-', '');
            handleEnvironmentSpecificSave(envId, data);
        } else {
            handleTabSave(tabKey, data);
        }
    };

    const getFormDataForTab = (tabKey: string) => {
        if (tabKey.includes('environments-security-csp-')) {
            const envId = tabKey.replace('environments-security-csp-', '');
            const envData =
                formData[tabKey] || getEnvironmentSpecificData(processedSettings, envId);
            return envData;
        }
        const formDataForTab = formData[tabKey];
        const fallbackTabData = getTabData(tabKey);

        // Use fallback data if formData is empty or undefined
        const tabData =
            formDataForTab && Object.keys(formDataForTab).length > 0
                ? formDataForTab
                : fallbackTabData;

        return tabData;
    };

    if (!settings) {
        return <div>Loading settings...</div>;
    }

    return (
        <div className="tabbed-settings-form">
            <TabRenderer
                tabs={tabs}
                activeTab={activeTab}
                activeSubTabs={activeSubTab}
                formData={formData}
                rootFormData={processedSettings}
                onTabChange={handleTabChange}
                onSubTabChange={handleSubTabChange}
                onFormChange={handleFormChange}
                onFormSubmit={handleFormSubmit}
                onFormError={() => {
                    // Handle form error
                    setSettingsSaved(false);
                }}
                getFormData={getFormDataForTab}
            />

            <div className="settings-actions">
                <button
                    className="save-settings-button btn btn-primary"
                    onClick={handleSave}
                    type="button"
                >
                    Save
                </button>
                {settingsSaved && (
                    <span className="settings-saved-message text-success ml-2">
                        Saved successfully!
                    </span>
                )}
            </div>
        </div>
    );
};

export default TabbedSettingsForm;
